import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  DocumentIcon,
  DeviceMobileIcon,
  RefreshIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/outline";
import { getOutputFormatDisplayText } from "pages/crawler/utils/outputFormatUtils";
import StatsCard, { StatItem } from "components/StatsCard";

interface JobStats {
  urlsCrawled: number;
  inProgress: number;
  cancelled: number;
  failed: number;
  totalRequests?: number;
}

interface JobDetails {
  stats: JobStats;
  outputFormat?: string;
  autoparse?: boolean;
  deviceType?: string;
  retry404?: boolean;
  totalCost?: number;
}

// Helper function to determine first column data
const getFirstColumnData = (details: JobDetails, showCreditsFirst: boolean): StatItem => {
  if (showCreditsFirst) {
    return {
      icon: CurrencyDollarIcon,
      iconClassName: "w-4 h-4 text-green-600",
      label: "Credits spent",
      value: details.totalCost || 0,
    };
  }
  return {
    icon: CheckCircleIcon,
    iconClassName: "w-4 h-4 text-green-500",
    label: "URLs crawled",
    value: details.stats.urlsCrawled,
  };
};

// Helper function to determine second column data
const getSecondColumnData = (details: JobDetails, showCreditsFirst: boolean, noJobsInProgress: boolean): StatItem => {
  if (showCreditsFirst) {
    return {
      icon: CheckCircleIcon,
      iconClassName: "w-4 h-4 text-green-500",
      label: "URLs crawled",
      value: details.stats.urlsCrawled,
    };
  }
  if (noJobsInProgress) {
    return {
      icon: CurrencyDollarIcon,
      iconClassName: "w-4 h-4 text-green-600",
      label: "Credits spent",
      value: details.totalCost || 0,
    };
  }
  return {
    icon: details.stats.inProgress > 0 ? undefined : ClockIcon,
    iconClassName: "w-4 h-4 text-blue-500",
    label: "In progress",
    value: details.stats.inProgress,
    showSpinner: details.stats.inProgress > 0,
  };
};

export const JobStatsCard = ({ details }: { details: JobDetails | null }) => {
  if (!details) return null;

  // Check if no jobs are in progress (completed, cancelled, or failed)
  const noJobsInProgress = details.stats.inProgress === 0;

  // Show credits spent first only when nothing is running AND no URLs crawled
  const showCreditsFirst = details.stats.inProgress === 0 && details.stats.urlsCrawled === 0;

  const stats: StatItem[] = [
    getFirstColumnData(details, showCreditsFirst),
    getSecondColumnData(details, showCreditsFirst, noJobsInProgress),
    {
      icon: XCircleIcon,
      iconClassName: "w-4 h-4 text-gray-500",
      label: "Cancelled",
      value: details.stats.cancelled,
    },
    {
      icon: ExclamationCircleIcon,
      iconClassName: "w-4 h-4 text-red-500",
      label: "Failed",
      value: details.stats.failed,
    },
  ];

  return (
    <StatsCard
      stats={stats}
      parameters={
        <>
          <span className="inline-flex items-center px-3 py-1.5 text-xs rounded-md">
            <DocumentIcon className="w-3 h-3 mr-1.5" />
            Output format: {getOutputFormatDisplayText(details.outputFormat, details.autoparse)}
          </span>
          <span className="inline-flex items-center px-3 py-1.5 text-xs rounded-md">
            <DeviceMobileIcon className="w-3 h-3 mr-1.5" />
            Device type: {details.deviceType || "Desktop"}
          </span>
          <span className="inline-flex items-center px-3 py-1.5 text-xs rounded-md">
            <RefreshIcon className="w-3 h-3 mr-1.5" />
            Retry 404: {details.retry404 ? "Enabled" : "Disabled"}
          </span>
        </>
      }
    />
  );
};

export default JobStatsCard;
